import { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import useStyles from './styles';
import { RecursiveKeyOf } from '../../../../utils/types';
import CondominiumEntity from '../../../../entities/condominium.entity';
import { toAddress } from '../../../../entities/address.entity';
import { set } from '../../../../utils/objects';
import Form from './components/Form';
import CondominiumContext from '../../../../contexts/CondominiumContext';
import { clone } from '../../../../utils/helpers';
import { IconTitle } from '../../../../components';
import { Cleaning, CondominiumType, Furniture, Laundry, Maid } from '../../../../entities';
import {
  useCleanings,
  useCondominiums,
  useFetch,
  useFurnitures,
  useLaundries,
  useMaids,
  useStorage,
} from '../../../../hooks';

const initialCondominium = {
  type: CondominiumType.Residential,
  isHelpEnabled: true,
  isSignupEnabled: true,
  isEmergencyEnabled: true,
  isFeedEnabled: true,
  isChatEnabled: true,
  isGuestsEnabled: true,
  isReservationsEnabled: true,
  isBikesEnabled: true,
  isPackagesEnabled: true,
  reservationMaxPeriod: 30,
};

export const BasicInfo = (): JSX.Element => {
  const styles = useStyles();
  const history = useNavigate();
  const {
    condominium: loadedCondominium,
    loading,
    setCondominium: setContextCondominium,
  } = useContext(CondominiumContext);

  const [fileToUpload, setFileToUpload] = useState<File | null>();
  const { enqueueSnackbar } = useSnackbar();
  const [laundries, setLaundries] = useState<Laundry[]>([]);
  const [furnitures, setFurnitures] = useState<Furniture[]>([]);
  const [cleanings, setCleanings] = useState<Cleaning[]>([]);
  const [maids, setMaids] = useState<Maid[]>([]);
  const [condominium, setCondominium] = useState<CondominiumEntity>(
    loadedCondominium || (initialCondominium as CondominiumEntity)
  );
  const laundryRepository = useLaundries();
  const furnitureRepository = useFurnitures();
  const cleaningRepository = useCleanings();
  const maidsRepository = useMaids();
  const laundryList = useFetch(laundryRepository.fetchAll);
  const furnitureList = useFetch(furnitureRepository.fetchAll);
  const cleaningList = useFetch(cleaningRepository.fetchAll);
  const maidList = useFetch(maidsRepository.fetchAll);

  useEffect(() => {
    setLaundries(laundryList.data ?? []);
    setFurnitures(furnitureList.data ?? []);
    setCleanings(cleaningList.data ?? []);
    setMaids(maidList.data ?? []);
  }, [laundryList.data, furnitureList.data, cleaningList.data, maidList.data]);

  useEffect(() => {
    setCondominium(clone(loadedCondominium) || (initialCondominium as CondominiumEntity));
  }, [loadedCondominium]);

  const handleFieldChange = (key: RecursiveKeyOf<CondominiumEntity>, value: string | null | number | boolean) => {
    const newCondominium = set(condominium, key, value);
    setCondominium(newCondominium);
  };

  const handleUpload = (file: File | null) => {
    setFileToUpload(file);
  };

  const storage = useStorage();
  const condominiumsRepository = useCondominiums();

  const handleFetchCondominiumAvatar = async (id: string): Promise<string | null | undefined> => {
    if (fileToUpload === undefined) {
      return condominium.avatarUrl;
    }

    if (!fileToUpload) {
      return null;
    }

    const extension = fileToUpload.name.split('.').pop();
    const avatarUrl = await storage.upload(`/condominiums/${id}.${extension}`, fileToUpload);
    handleFieldChange('avatarUrl', avatarUrl);
    return avatarUrl;
  };

  const handleCreateCondominium = async () => {
    const { id } = await condominiumsRepository.create({
      ...condominium,
      type: condominium.type ?? CondominiumType.Residential,
      address: toAddress(condominium.fullAddress!),
    });
    await handleFetchCondominiumAvatar(id);
    history(`/condominiums/${id}`);
  };

  const updateCondominium = async () => {
    await condominiumsRepository.update({
      ...condominium,
      address: toAddress(condominium.fullAddress!),
      avatarUrl: await handleFetchCondominiumAvatar(condominium.id),
    });
    setContextCondominium(condominium);
  };

  const onSubmit = async () => {
    try {
      if (condominium.id) {
        await updateCondominium();
      } else {
        await handleCreateCondominium();
      }

      enqueueSnackbar('Condomínio salvo com sucesso!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Não foi possível salvar, tente novamente mais tarde', { variant: 'error' });
    }
  };

  if (loading) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <IconTitle title="Dados do condomínio" />
      <Form
        laundries={laundries}
        furnitures={furnitures}
        cleanings={cleanings}
        maids={maids}
        condominium={condominium}
        fileToUpload={fileToUpload}
        onSubmit={onSubmit}
        handleFieldChange={handleFieldChange}
        handleUpload={handleUpload}
      />
    </div>
  );
};

export default BasicInfo;
